/** Settings → Custom fields: definitions per owner type. Owner, namespace, key and type are fixed after creation. */
import React, { useMemo, useState } from 'react';
import { Button, Collapse, Empty, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { CopyLine, listOf, toastErr, useLoad, useSite } from './common';

const { Text } = Typography;
const TEMPLATED = new Set(['product', 'collection']); // customer and order fields are not in the storefront data
const keySlug = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);

const ShopCustomFields = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const { data, loading, reload } = useLoad(() => api.get('/metafield-definitions'), [api]);
    const items = useMemo(() => listOf(data), [data]);
    const [owner, setOwner] = useState();
    const [edit, setEdit] = useState(null); // {} = new, row = rename
    const [saving, setSaving] = useState(false);
    const [keyTouched, setKeyTouched] = useState(false);
    const [form] = Form.useForm();

    const OWNERS = [
        { value: 'product', label: t('Product', { context: 'shop' }) }, { value: 'collection', label: t('Collection') },
        { value: 'customer', label: t('Customer') }, { value: 'order', label: t('Order') },
    ];
    const TYPES = [
        { value: 'text', label: t('Text') }, { value: 'number', label: t('Number') }, { value: 'boolean', label: t('Yes / No') }, { value: 'url', label: t('URL') },
        { value: 'date', label: t('Date') }, { value: 'json', label: 'JSON' }, { value: 'rich_text', label: t('Rich text') },
    ];
    const name = (list, v) => list.find((x) => x.value === v)?.label || v;

    const open = (row) => {
        setEdit(row || {});
        setKeyTouched(false);
        form.setFieldsValue(row ? { name: row.name } : { owner: owner || 'product', type: 'text', name: '', key: '', namespace: 'custom' });
    };
    const submit = async () => {
        const v = await form.validateFields().catch(() => null);
        if (!v) return;
        setSaving(true);
        try {
            if (edit.id) await api.put(`/metafield-definitions/${edit.id}`, { name: v.name });
            else await api.post('/metafield-definitions', v);
            message.success(t('Saved'));
            setEdit(null);
            reload();
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };
    const remove = async (row) => {
        try { await api.del(`/metafield-definitions/${row.id}`); message.success(t('Deleted')); reload(); } catch (e) { toastErr(e); }
    };

    const shown = owner ? items.filter((x) => x.owner === owner) : items;
    const columns = [
        { title: t('Name'), dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
        { title: t('Applies to'), dataIndex: 'owner', width: 110, render: (v) => <Tag>{name(OWNERS, v)}</Tag> },
        { title: t('Type'), dataIndex: 'type', width: 100, render: (v) => name(TYPES, v) },
        {
            title: t('Template path'), key: 'path', render: (_, r) => (TEMPLATED.has(r.owner)
                ? <CopyLine value={`${r.owner}.metafields.${r.namespace}.${r.key}`} />
                : <Tooltip title={t('Shown in the console only, not in templates')}><Text type="secondary">{r.namespace}.{r.key}</Text></Tooltip>),
        },
        {
            title: '', key: 'act', width: 90, align: 'right', render: (_, r) => (
                <Space size={0}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => open(r)} aria-label={t('Edit')} />
                    <Popconfirm title={t('Delete this field and its values?')} okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={t('Delete')} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <Select allowClear placeholder={t('Applies to')} value={owner} onChange={setOwner} options={OWNERS} style={{ width: 160 }} />
                <div style={{ flex: 1 }} />
                <Button type="primary" icon={<PlusOutlined />} onClick={() => open()}>{t('Add field')}</Button>
            </div>
            <Table size="small" rowKey="id" loading={loading} dataSource={shown} columns={columns} pagination={false} scroll={{ x: 680 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No custom fields yet')} /> }} />
            <Modal open={!!edit} title={edit?.id ? t('Edit field') : t('Add field')} onCancel={() => setEdit(null)} onOk={submit} confirmLoading={saving} okText={t('Save')} forceRender width={460}>
                <Form form={form} layout="vertical" requiredMark={false}
                    onValuesChange={(ch) => { if (!edit?.id && 'name' in ch && !keyTouched) form.setFieldsValue({ key: keySlug(ch.name) }); }}>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true }]}><Input maxLength={191} placeholder="Care instructions" autoFocus /></Form.Item>
                    {edit && !edit.id && (
                        <>
                            <Space wrap align="start">
                                <Form.Item name="owner" label={t('Applies to')}><Select options={OWNERS} style={{ width: 180 }} /></Form.Item>
                                <Form.Item name="type" label={t('Type')}><Select options={TYPES} style={{ width: 180 }} /></Form.Item>
                            </Space>
                            <Form.Item name="key" label={t('Key')} rules={[{ required: true, pattern: /^[a-z0-9_]+$/, message: 'a-z, 0-9, _' }]} tooltip={t('Cannot be changed later')}>
                                <Input maxLength={64} onChange={() => setKeyTouched(true)} />
                            </Form.Item>
                            <Collapse ghost size="small" style={{ margin: '0 -16px' }} items={[{
                                key: 'adv', forceRender: true, label: t('Advanced'),
                                children: <Form.Item name="namespace" label={t('Namespace')} style={{ marginBottom: 0 }} rules={[{ pattern: /^[a-z0-9_]*$/, message: 'a-z, 0-9, _' }]}><Input maxLength={32} /></Form.Item>,
                            }]} />
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default ShopCustomFields;
